import React, { useMemo, useState, useEffect } from "react";
import ReactMarkdown from "react-markdown";
import "./App.css";
import LoginPage from "./LoginPage";

const API_BASE = process.env.REACT_APP_API_URL || "http://127.0.0.1:5001";
/** Score → bucket (tweak thresholds if you want) */
function riskBucket(score) {
  const s = Number(score) || 0;
  if (s >= 70) return "CRITICAL";
  if (s >= 40) return "SUSPICIOUS";
  if (s > 0) return "REVIEW";
  return "CLEAN";
}

function App() {
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);

  const [path, setPath] = useState("");
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  // ✅ NEW: triage filter state
  const [riskFilter, setRiskFilter] = useState("ALL");

  const [chatInput, setChatInput] = useState("");
  const [chatHistory, setChatHistory] = useState([
    {
      role: "ai",
      text: "### Forensic Assistant Online\nFirst add a directory path in the search bar to the left. Then ask for a detailed breakdown of any of the files or questions about any types of extensions.",
    },
  ]);

  useEffect(() => {
    const token = localStorage.getItem("authToken");
    const user = localStorage.getItem("user");
    if (token && user) {
      setIsLoggedIn(true);
      setCurrentUser(JSON.parse(user));
    }
  }, []);

  const handleLoginSuccess = () => {
    const user = JSON.parse(localStorage.getItem("user"));
    setCurrentUser(user);
    setIsLoggedIn(true);
  };

  const handleLogout = () => {
    const token = localStorage.getItem("authToken");
    if (token) {
      fetch(`${API_BASE}/auth/logout`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
      }).catch(() => {});
    }

    localStorage.removeItem("authToken");
    localStorage.removeItem("user");
    setIsLoggedIn(false);
    setCurrentUser(null);
    setResults([]);
    setPath("");
    setRiskFilter("ALL");
  };

  const handleScan = async () => {
    setLoading(true);
    setError("");
    setResults([]);

    try {
      const token = localStorage.getItem("authToken");
      const response = await fetch(`${API_BASE}/scan`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ path: path }),
      });

      const data = await response.json();

      if (response.ok) {
        // Keep highest scores at the top
        const sortedData = Array.isArray(data)
          ? data.sort((a, b) => b.Score - a.Score)
          : [];
        setResults(sortedData);
        setRiskFilter("ALL"); // reset filter each scan
      } else {
        if (response.status === 401) {
          handleLogout();
          setError("Session expired. Please login again.");
        } else {
          setError(data.error || "Scan failed");
        }
      }
    } catch (err) {
      setError("Could not connect to server.");
    }

    setLoading(false);
  };

  // ✅ NEW: triage counts
  const counts = useMemo(() => {
    const c = { CRITICAL: 0, SUSPICIOUS: 0, REVIEW: 0, CLEAN: 0 };
    for (const f of results) c[riskBucket(f.Score)] += 1;
    return c;
  }, [results]);

  // ✅ NEW: filtered rows
  const visibleResults = useMemo(() => {
    if (riskFilter === "ALL") return results;
    return results.filter((f) => riskBucket(f.Score) === riskFilter);
  }, [results, riskFilter]);

  const sendToAI = async () => {
    if (!chatInput.trim()) return;

    const userMsg = { role: "user", text: chatInput };
    setChatHistory((prev) => [...prev, userMsg]);
    setChatInput("");

    try {
      const response = await fetch(`${API_BASE}/chat`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          message: userMsg.text,
          scan_context: results,
        }),
      });

      const data = await response.json();
      setChatHistory((prev) => [...prev, { role: "ai", text: data.reply }]);
    } catch (err) {
      setChatHistory((prev) => [
        ...prev,
        {
          role: "ai",
          text: "**AI connection error.** Could not reach server.",
        },
      ]);
    }
  };

  if (!isLoggedIn) {
    return <LoginPage onLoginSuccess={handleLoginSuccess} apiBase={API_BASE} />;
  }

  return (
    <div className="main-wrapper">
      {/* LEFT SIDE: SCANNER */}
      <div className="container">
        <div className="header">
          <h1 className="title">
            Suspicion<span>Sort</span>
            <span className="beta">BETA</span>
          </h1>

          <div className="user-info">
            <span>Welcome, {currentUser?.username}!</span>
            <button className="logout-btn" onClick={handleLogout}>
              LOGOUT
            </button>
          </div>
        </div>

        <div className="search-box">
          <input
            type="text"
            placeholder="Enter local directory path..."
            value={path}
            onChange={(e) => setPath(e.target.value)}
          />
          <button onClick={handleScan} disabled={loading}>
            {loading ? "ANALYZING..." : "RUN SCAN"}
          </button>
        </div>

        {error && <div className="error">{error}</div>}

        {/* ✅ NEW: triage bar */}
        {results.length > 0 && (
          <>
            <div className="triageBar">
              <button
                className={`triageBtn ${riskFilter === "ALL" ? "active" : ""}`}
                onClick={() => setRiskFilter("ALL")}
              >
                📁 All <span className="triageCount">{results.length}</span>
              </button>

              <button
                className={`triageBtn ${
                  riskFilter === "CRITICAL" ? "active" : ""
                }`}
                onClick={() => setRiskFilter("CRITICAL")}
              >
                🔴 Critical{" "}
                <span className="triageCount">{counts.CRITICAL}</span>
              </button>

              <button
                className={`triageBtn ${
                  riskFilter === "SUSPICIOUS" ? "active" : ""
                }`}
                onClick={() => setRiskFilter("SUSPICIOUS")}
              >
                🟠 Suspicious{" "}
                <span className="triageCount">{counts.SUSPICIOUS}</span>
              </button>

              <button
                className={`triageBtn ${
                  riskFilter === "REVIEW" ? "active" : ""
                }`}
                onClick={() => setRiskFilter("REVIEW")}
              >
                🟡 Review <span className="triageCount">{counts.REVIEW}</span>
              </button>

              <button
                className={`triageBtn ${
                  riskFilter === "CLEAN" ? "active" : ""
                }`}
                onClick={() => setRiskFilter("CLEAN")}
              >
                🟢 Clean <span className="triageCount">{counts.CLEAN}</span>
              </button>
            </div>

            <div className="triageHint">
              Showing {visibleResults.length} of {results.length} results
            </div>
          </>
        )}

        <div className="results-area">
          {visibleResults.length > 0 && (
            <table>
              <thead>
                <tr>
                  <th>Score</th>
                  <th>File Name</th>
                  <th>Flags</th>
                  <th>Entropy</th>
                </tr>
              </thead>

              <tbody>
                {visibleResults.map((file, index) => {
                  // Keep your existing row classes (danger/warning/safe)
                  let rowClass = "row-safe";
                  if (file.Score >= 70) rowClass = "row-danger";
                  else if (file.Score >= 40) rowClass = "row-warning";

                  return (
                    <tr key={index} className={rowClass}>
                      <td className="score-cell">{file.Score}</td>
                      <td>{file.File}</td>
                      <td style={{ color: file.Flags ? "#ff9f43" : "#666" }}>
                        {file.Flags || "CLEAN"}
                      </td>
                      <td>{file.Entropy}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {/* RIGHT SIDE: AI SIDEBAR */}
      <div className="ai-sidebar">
        <div className="sidebar-header">AI ANALYST</div>

        <div className="chat-window">
          {chatHistory.map((msg, i) => (
            <div key={i} className={`chat-bubble ${msg.role}`}>
              <ReactMarkdown>{msg.text}</ReactMarkdown>
            </div>
          ))}
        </div>

        <div className="sidebar-input">
          <input
            value={chatInput}
            onChange={(e) => setChatInput(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && sendToAI()}
            placeholder="Ask about these files..."
          />
          <button onClick={sendToAI}>SEND</button>
        </div>
      </div>
    </div>
  );
}

export default App;
